import type { GameId } from '@ringmaster/shared';
import { GameSelectScreen as FullscreenGameSelect } from '../discord/GameSelectScreen.js';

/**
 * GameSelectScreen — fullscreen "Change game" surface (hand/stage/discord
 * lobbies + the END screen's Change-game handshake).
 *
 * Thin wrapper over the ONE canonical picker (discord/GameSelectScreen.tsx —
 * the Activity's "Choose tonight's game" boot gate) in its 'switch' mode, so
 * both surfaces share the SAME design: ember particles, breakout character
 * cards, badges, animated headline. This wrapper adds only the pick/close
 * semantics: the back pill / Escape / re-picking the current game closes
 * without changing anything; tapping another live game fires onPick, then
 * closes.
 *
 * Host-gated by the caller (SWITCH_GAME is host-only / LOBBY+END-only) — this
 * component itself has no opinion on who may open it.
 */
export function GameSelectScreen({
  currentGameId,
  onPick,
  onClose,
}: {
  currentGameId: string;
  onPick: (gameId: GameId) => void;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose tonight's game"
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#080808', overflowY: 'auto' }}
    >
      <FullscreenGameSelect
        seated
        mode="switch"
        currentGameId={currentGameId}
        onClose={onClose}
        onEnter={(gameId) => {
          // 'switch' mode only ever fires from a card tap, so an id is always
          // present; the guard keeps a no-id call a plain close, never a switch.
          if (gameId && gameId !== currentGameId) onPick(gameId as GameId);
          onClose();
        }}
      />
    </div>
  );
}
